import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion'; // Suggest adding: npm install framer-motion
import { setActiveItem } from '../features/nav/navSlice';
import { setLoggedInUser } from '../features/auth/authSlice';
import { getUsersFromUsersTableById } from "../api/reusableApis";
import {
  FiGrid, FiChevronDown, FiMenu, FiX, FiLogOut, FiUser
} from 'react-icons/fi';
import { TbBrandBooking, TbPackages, TbLogout } from "react-icons/tb";
import { CgProfile } from "react-icons/cg";
import { FaHouseUser } from "react-icons/fa";
import { LuAlarmClockCheck } from "react-icons/lu";
import { RiFunctionAddLine } from "react-icons/ri";
import { GrMapLocation } from "react-icons/gr";
import logoImg from '../images/logo.jpg';

function Nav() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [navItems, setNavItems] = useState([]);
  const [openMenu, setOpenMenu] = useState(null);

  const activeItem = useSelector((state) => state.nav.activeItem);
  const currentLoggedInUser = useSelector((state) => state.auth.currentUser);
  const dispatch = useDispatch();
  const appName = process.env.REACT_APP_NAME || 'best surprise';

  useEffect(() => {
    const fetchUserData = async () => {
      const auth = window.catalyst.auth;
      const response = await auth.isUserAuthenticated();

      if (response.status === 200) {
        const data = response.content;
        const userDetails = await getUsersFromUsersTableById(data.user_id);
        data.users = userDetails.user;
        dispatch(setLoggedInUser(data));

        const isAdmin = data['role_details']['role_name'] === 'App Administrator';

        const commonItems = [
          { icon: FiGrid, label: 'Dashboard' },
          {
            icon: TbBrandBooking,
            label: "Bookings",
            children: [
              { label: "Create Booking" },
              { label: "List Bookings" },
              { label: "Booking Table" },
            ]
          }
        ];

        const adminItems = isAdmin ? [
          { icon: FaHouseUser, label: 'Manage users' },
          { icon: GrMapLocation, label: "Manage Branch" },
          { icon: RiFunctionAddLine, label: "Addon's" },
          { icon: LuAlarmClockCheck, label: "Manage time-slots" },
          { icon: TbPackages, label: "Packages" },
        ] : [];

        const footerItems = [
          { icon: CgProfile, label: 'Profile' },
          { icon: TbLogout, label: 'Logout', isLogout: true }
        ];

        setNavItems([...commonItems, ...adminItems, ...footerItems]);
      }
    };
    fetchUserData();
  }, [dispatch]);

  return (
    <>
      {/* Mobile Top Bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-white/80 backdrop-blur-md border-b border-gray-100 px-5 py-3 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setMenuOpen(true)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FiMenu className="text-2xl text-gray-700" />
          </button>
          <img src={logoImg} alt="logo" className='h-9 w-9 rounded-full object-cover border border-purple-100' />
          <span className="font-bold text-lg text-[#72398E]">{appName}</span>
        </div>
      </div>

      {/* Backdrop */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setMenuOpen(false)}
            className='fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 lg:hidden'
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky top-0 left-0 z-50 h-screen w-[280px] bg-white border-r border-gray-100
        flex flex-col transition-all duration-300 ease-in-out
        ${menuOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
      >
        {/* Header */}
        <div className='p-6 flex items-center justify-between'>
          <div className="flex items-center gap-3">
            <img src={logoImg} alt="logo" className='h-10 w-10 rounded-xl shadow-inner border border-gray-50' />
            <h1 className='text-xl font-black text-[#72398E] tracking-tight'>{appName}</h1>
          </div>
          <button onClick={() => setMenuOpen(false)} className='lg:hidden text-gray-400 hover:text-gray-600'>
            <FiX size={20} />
          </button>
        </div>

        {/* Navigation */}
        <nav className='flex-1 px-4 space-y-1 overflow-y-auto custom-scrollbar'>
          {navItems.map((item, index) => (
            <div key={index} className="py-0.5">
              <button
                onClick={() => {
                  if (item.children) {
                    setOpenMenu(openMenu === item.label ? null : item.label);
                  } else {
                    dispatch(setActiveItem(item.label));
                    setMenuOpen(false);
                  }
                }}
                className={`flex items-center justify-between w-full px-4 py-3 rounded-xl transition-all duration-200 group
                ${activeItem === item.label
                    ? 'bg-purple-50 text-[#72398E]'
                    : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'}`}
              >
                <div className="flex items-center gap-3">
                  <item.icon className={`text-xl ${activeItem === item.label ? 'text-[#72398E]' : 'group-hover:text-gray-700'}`} />
                  <span className="font-medium">{item.label}</span>
                </div>
                {item.children && (
                  <FiChevronDown className={`transition-transform duration-200 ${openMenu === item.label ? 'rotate-180' : ''}`} />
                )}
              </button>

              {/* Submenu with Animation */}
              <AnimatePresence>
                {item.children && openMenu === item.label && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden ml-6 mt-1 border-l-2 border-gray-50"
                  >
                    {item.children.map((child, i) => (
                      <button
                        key={i}
                        onClick={() => {
                          dispatch(setActiveItem(child.label));
                          setMenuOpen(false);
                        }}
                        className={`block w-full text-left px-6 py-2.5 text-sm transition-colors
                        ${activeItem === child.label ? 'text-[#72398E] font-bold' : 'text-gray-500 hover:text-gray-800'}`}
                      >
                        {child.label}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </nav>

        {/* User Profile Section */}
        <div className='p-4 border-t border-gray-50 bg-gray-50/50'>
          <div className='flex items-center gap-3 p-2 rounded-2xl'>
            <img
              src={currentLoggedInUser?.['users']?.['user_img_url'] || 'https://ui-avatars.com/api/?background=72398E&color=fff&name=User'}
              alt='profile'
              className='h-10 w-10 rounded-full border-2 border-white shadow-sm object-cover'
            />
            <div className="flex-1 min-w-0">
              <p className='text-sm font-bold text-gray-800 truncate'>
                {currentLoggedInUser?.['first_name']} {currentLoggedInUser?.['last_name']}
              </p>
              <p className='text-xs text-gray-500 truncate'>
                {currentLoggedInUser?.['email_id']}
              </p>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}

export default Nav;